// Migration maps for category name changes.
//
// RESOLVED (ClickUp 86badfkb1 — McKayla decision 2026-06-18): "Ultimate Financer"
// (the "Balanced" 6th type) is OUT. The 5-personality org chart is canonical
// (Dream Chaser, Wealth Architect, Safety Seeker, Generous Altruist, Freedom Finder).
// "Freedom Finder" carries the "The Balanced" descriptor, so this rename map is the
// correct path for any legacy stored result still tagged "Ultimate Financer" — it
// resolves such data to the canonical Freedom Finder type. KEEP this map (it is data
// migration for old results, not a live 6th personality). The live model already
// exposes exactly the 5 canonical types everywhere (see PERSONALITY_TYPES).
export const MAIN_CATEGORY_MIGRATION_MAP = {
  "Ultimate Financer": "Freedom Finder",
} as const;

export const SPECIFIC_CATEGORY_MIGRATION_MAP = {
  "Impulse Spender": "Spontaneous Spender",
  "Anxious Saver": "Cautious Saver",
  "Analyst Investor": "Analytic Investor",
} as const;

// Type definitions for better type safety
export interface ResultsData {
  // The three sub-personality fields below are OPTIONAL as of 2026-08-20
  // (ClickUp 86bb0f9vz). New results never carry them — the sub-quiz is gone —
  // but results stored in localStorage before that date still do, and
  // `specificDominantCategory` is the key that resolves a pre-existing user's
  // workbook exercises. Do not delete them: that would strip the field on the
  // way through and silently empty an existing user's saved workbook.
  answers: any[];
  specificAnswers?: any[];
  categoryScores: Record<string, number>;
  specificCategoryScores?: Record<string, number>;
  dominantCategory: string;
  specificDominantCategory?: string;
  sfAccount?: string | null;
  quizId?: number;
  leadId?: number;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  from?: string;
}

/**
 * Migrates a single category name from old to new format
 */
export function migrateCategoryName(
  categoryName: string,
  isSpecific: boolean = false
): string {
  if (isSpecific) {
    return (
      SPECIFIC_CATEGORY_MIGRATION_MAP[
        categoryName as keyof typeof SPECIFIC_CATEGORY_MIGRATION_MAP
      ] || categoryName
    );
  } else {
    return (
      MAIN_CATEGORY_MIGRATION_MAP[
        categoryName as keyof typeof MAIN_CATEGORY_MIGRATION_MAP
      ] || categoryName
    );
  }
}

/*
  * Migrates quiz results object (category scores)
*/
export function migrateQuizResults(
  results: Record<string, number>,
  isSpecific: boolean = false
): Record<string, number> {
  if (!results || typeof results !== "object") {
    return results;
  }

  const migratedResults: Record<string, number> = {};

  // First pass: process entries that need migration (old category names)
  const migrationMap = isSpecific ? SPECIFIC_CATEGORY_MIGRATION_MAP : MAIN_CATEGORY_MIGRATION_MAP;
  const processedOldNames = new Set<string>();

  for (const [categoryName, score] of Object.entries(results)) {
    const migratedName = migrateCategoryName(categoryName, isSpecific);
    
    if (migrationMap[categoryName as keyof typeof migrationMap]) {
      // This is an old category name that needs migration
      migratedResults[migratedName] = score;
      processedOldNames.add(categoryName);
    }
  }

  // Second pass: process entries that don't need migration, but only if not conflicting
  for (const [categoryName, score] of Object.entries(results)) {
    if (!migrationMap[categoryName as keyof typeof migrationMap] && !processedOldNames.has(categoryName)) {
      // Only add if we haven't already set this key from migration
      if (!(categoryName in migratedResults)) {
        migratedResults[categoryName] = score;  
      }
    }
  }

  return migratedResults;
}

/**
 * Migrates complete ResultsData object
 */
export function migrateResultsData(resultsData: ResultsData): ResultsData {
  if (!resultsData || typeof resultsData !== "object") {
    return resultsData;
  }

  const migrated: ResultsData = {
    ...resultsData,
    // Migrate category scores
    categoryScores: migrateQuizResults(resultsData.categoryScores, false),
    // The two sub-personality fields became optional on 2026-08-20 (ClickUp
    // 86bb0f9vz). A result created after that date has neither, and must pass
    // through as absent — migrating `undefined` into `{}` / "" would turn "this
    // user never took a sub-quiz" into "this user scored nothing", which reads
    // downstream as a real-but-empty result.
    ...(resultsData.specificCategoryScores !== undefined && {
      specificCategoryScores: migrateQuizResults(
        resultsData.specificCategoryScores,
        true
      ),
    }),
    // Migrate dominant categories
    dominantCategory: migrateCategoryName(resultsData.dominantCategory, false),
    ...(resultsData.specificDominantCategory !== undefined && {
      specificDominantCategory: migrateCategoryName(
        resultsData.specificDominantCategory,
        true
      ),
    }),
  };

  return migrated;
}

/**
 * Checks if data contains old category names that need migration
 */
export function shouldMigrate(data: any): boolean {
  if (!data || typeof data !== "object") {
    return false;
  }

  // Check for old main category names
  const checkMainCategories = (obj: any) => {
    if (!obj) return false;
    return Object.keys(MAIN_CATEGORY_MIGRATION_MAP).some((oldName) =>
      typeof obj === "object" ? oldName in obj : obj === oldName
    );
  };

  // Check for old specific category names
  const checkSpecificCategories = (obj: any) => {
    if (!obj) return false;
    return Object.keys(SPECIFIC_CATEGORY_MIGRATION_MAP).some((oldName) =>
      typeof obj === "object" ? oldName in obj : obj === oldName
    );
  };

  // Check various fields that might contain category names
  return Boolean(
    checkMainCategories(data.categoryScores) ||
    checkSpecificCategories(data.specificCategoryScores) ||
    checkMainCategories(data.dominantCategory) ||
    checkSpecificCategories(data.specificDominantCategory) ||
    // Check nested quiz results format
    (data.results?.mainQuiz && checkMainCategories(data.results.mainQuiz)) ||
    (data.results?.specificQuiz &&
      checkSpecificCategories(data.results.specificQuiz))
  );
}

/**
 * Validates that migrated data contains only current category names
 */
export function validateMigratedData(data: ResultsData): boolean {
  if (!data) return false;

  try {
    const allMainCategories = Object.keys(data.categoryScores || {});
    const allSpecificCategories = Object.keys(
      data.specificCategoryScores || {}
    );

    // Check if any old category names still exist
    const hasOldMainNames = allMainCategories.some((name) =>
      Object.keys(MAIN_CATEGORY_MIGRATION_MAP).includes(name)
    );
    const hasOldSpecificNames = allSpecificCategories.some((name) =>
      Object.keys(SPECIFIC_CATEGORY_MIGRATION_MAP).includes(name)
    );

    return !hasOldMainNames && !hasOldSpecificNames;
  } catch (error) {
    console.error("Error validating migrated data:", error);
    return false;
  }
}

/**
 * Returns a safe default category if migration fails
 */
export function getFallbackCategory(
  invalidCategory: string,
  isSpecific: boolean = false
): string {
  if (isSpecific) {
    return "Visionary Investor"; // Safe default specific category
  } else {
    return "Dream Chaser"; // Safe default main category
  }
}

/**
 * Migrates quiz results in database format (with nested mainQuiz/specificQuiz)
 */
export function migrateDatabaseQuizResults(quizData: any): any {
  if (!quizData || !quizData.results) {
    return quizData;
  }

  const migrated = { ...quizData };

  if (quizData.results.mainQuiz) {
    migrated.results.mainQuiz = migrateQuizResults(
      quizData.results.mainQuiz,
      false
    );
  }

  if (quizData.results.specificQuiz) {
    migrated.results.specificQuiz = migrateQuizResults(
      quizData.results.specificQuiz,
      true
    );
  }

  return migrated;
}

/**
 * Migrates localStorage keys that might contain category names
 */
export function migrateLocalStorageKeys(): void {
  try {
    // List of localStorage keys that might contain category data
    const keysToCheck = [
      "mpay_resultsData",
      "mpay_categoryPreferences",
      "mpay_lastCategory",
    ];

    keysToCheck.forEach((key) => {
      const stored = localStorage.getItem(key);
      if (stored) {
        try {
          const data = JSON.parse(stored);
          if (shouldMigrate(data)) {
            const migrated = migrateResultsData(data);
            localStorage.setItem(key, JSON.stringify(migrated));
            console.log(`Migrated localStorage key: ${key}`);
          }
        } catch (e) {
          // If it's not JSON, it might be a simple string category name
          const migratedString = migrateCategoryName(stored);
          if (migratedString !== stored) {
            localStorage.setItem(key, migratedString);
            console.log(`Migrated localStorage string: ${key}`);
          }
        }
      }
    });
  } catch (error) {
    console.error("Error migrating localStorage keys:", error);
  }
}
